import { useEffect, useMemo, useState } from "react";

interface Props {
  apiBase: string;
  imageUrl?: string;
  solutionImageUrl?: string;
  title: string;
  lat: number;
  lng: number;
}

interface EvidencePhoto {
  id: "before" | "after";
  url: string;
  alt: string;
  caption: string;
}

/** Resolve API-relative reporter files against the API host, not the static web host. */
export function resolveIncidentPhotoUrl(url: string, apiBase: string): string {
  if (/^https?:\/\//i.test(url)) return url;
  const base = apiBase.replace(/\/+$/, "");
  const path = url.startsWith("/") ? url : `/${url}`;
  return `${base}${path}`;
}

export function IncidentEvidence({ apiBase, imageUrl, solutionImageUrl, title, lat, lng }: Props) {
  const [expanded, setExpanded] = useState<EvidencePhoto | null>(null);
  const [failed, setFailed] = useState<Set<EvidencePhoto["id"]>>(() => new Set());

  const photos = useMemo<EvidencePhoto[]>(() => {
    const next: EvidencePhoto[] = [];
    if (imageUrl) {
      next.push({
        id: "before",
        url: resolveIncidentPhotoUrl(imageUrl, apiBase),
        alt: `Citizen photo for ${title}`,
        caption: "BEFORE · CITIZEN EVIDENCE",
      });
    }
    if (solutionImageUrl) {
      next.push({
        id: "after",
        url: resolveIncidentPhotoUrl(solutionImageUrl, apiBase),
        alt: `Resolution photo from staff for ${title}`,
        caption: "AFTER · STAFF PROOF",
      });
    }
    return next;
  }, [apiBase, imageUrl, solutionImageUrl, title]);

  useEffect(() => {
    setFailed(new Set());
    setExpanded(null);
  }, [imageUrl, solutionImageUrl]);

  useEffect(() => {
    if (!expanded) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      setExpanded(null);
    };
    window.addEventListener("keydown", closeOnEscape, true);
    return () => window.removeEventListener("keydown", closeOnEscape, true);
  }, [expanded]);

  const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${lat},${lng}`)}`;

  return (
    <section className="incident-evidence" aria-label="Task evidence and location">
      <div className="incident-evidence-head">
        <span className="eyebrow mono">Task evidence</span>
        <a
          className="incident-directions mono"
          href={directionsUrl}
          target="_blank"
          rel="noreferrer"
          title="Open turn-by-turn directions to this report"
        >
          GO TO TASK ↗
        </a>
      </div>

      {photos.length > 0 ? (
        <div className={`incident-photos incident-photos--${photos.length}`}>
          {photos.map((photo) => (
            <figure key={photo.id} className={`incident-photo incident-photo--${photo.id}`}>
              {failed.has(photo.id) ? (
                <div className="incident-photo-error mono" role="status">
                  PHOTO UNAVAILABLE · RETRY LATER
                </div>
              ) : (
                <button
                  type="button"
                  className="incident-photo-open"
                  onClick={() => setExpanded(photo)}
                  aria-label={`Enlarge ${photo.caption.toLowerCase()}`}
                  title="Click to inspect full size"
                >
                  <img
                    src={photo.url}
                    alt={photo.alt}
                    decoding="async"
                    onError={() => setFailed((current) => new Set(current).add(photo.id))}
                  />
                  <span className="incident-photo-zoom mono">EXPAND ↗</span>
                </button>
              )}
              <figcaption className="caption mono">{photo.caption}</figcaption>
            </figure>
          ))}
        </div>
      ) : (
        <div className="incident-photo-missing mono">NO PHOTO ATTACHED</div>
      )}

      <div className="incident-location mono">
        <span>PIN</span>
        <strong>{lat.toFixed(5)}, {lng.toFixed(5)}</strong>
      </div>

      {expanded && (
        <div className="incident-photo-lightbox" role="region" aria-label="Expanded task evidence">
          <button
            type="button"
            className="incident-photo-lightbox-close mono"
            onClick={() => setExpanded(null)}
            aria-label="Close expanded photo"
          >
            [ESC] CLOSE
          </button>
          <img src={expanded.url} alt={expanded.alt} />
          <span className="incident-photo-lightbox-caption mono">{expanded.caption}</span>
        </div>
      )}
    </section>
  );
}
